
import React, { useState } from "react";
import { Download, FileText, CheckCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { SectionHeader } from "@/components/ui/section-header";
import { GuidePreview } from "@/components/guides/GuidePreview";
import { Guide } from "@/types/guides";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

const benefits = [
  "Understand how to find the right nursing home for your needs",
  "Compare facilities across different counties in Ireland",
  "Learn about important questions to ask during your visits",
  "Get tips for a smooth transition to nursing home care",
  "Discover what to look for in care quality and staff qualifications",
];

export default function FairDealGuide() {
  const [showPreview, setShowPreview] = useState(false);
  const navigate = useNavigate();

  // Create a guide object that matches the Guide interface from @/types/guides.ts
  const guidePreview: Guide = {
    id: "1",  // Changed to string to match Guide type
    title: "Finding a Nursing Home in Ireland 2025",
    description: "Navigate the nursing home selection process with our comprehensive guide, written in plain English.",
    slug: "finding-nursing-home-ireland-2025",  // Added required property
    categories: ["Nursing Homes", "Care Guide", "Family Support"],
    content: "This comprehensive guide provides detailed information on how to find the right nursing home in Ireland. It covers everything from understanding your care needs, comparing facilities across different counties, asking the right questions during visits, and ensuring a smooth transition to nursing home care. The guide also includes insights about what to look for in terms of care quality and staff qualifications to help you make an informed decision for yourself or your loved one.",
    download_url: null,  // Added required property
    created_at: new Date().toISOString(),  // Added required property
    cover_image_url: "/lovable-uploads/58a0be05-26dc-4dff-beff-b488e2839fb6.png"
  };

  const handleDownloadClick = () => {
    // Store the guide info in session storage to identify which guide to download
    sessionStorage.setItem('downloadGuideId', guidePreview.id);
    sessionStorage.setItem('openDownloadModal', 'true');
    
    // Navigate to the guides page
    navigate('/guides');
    
    // Show toast notification - fixed for sonner compatibility
    toast.success("Redirecting to download", {
      description: "Please fill in your details to download the guide."
    });
  };

  return (
    <section className="py-20 bg-grandpal-beige">
      <div className="container">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <SectionHeader
              title="Finding a Nursing Home in Ireland 2025"
              subtitle="Navigate the nursing home selection process with our comprehensive guide, written in plain English."
            />

            <ul className="space-y-4 mb-8">
              {benefits.map((benefit, index) => (
                <li key={index} className="flex items-start">
                  <CheckCircle className="h-6 w-6 text-grandpal-green-dark mr-3 mt-0.5 flex-shrink-0" />
                  <span>{benefit}</span>
                </li>
              ))}
            </ul>

            <div className="flex flex-col sm:flex-row gap-4">
              <Button 
                className="bg-grandpal-green-dark hover:bg-grandpal-green-darkHover text-white flex items-center"
                size="lg"
                onClick={handleDownloadClick}
              >
                <Download className="mr-2 h-5 w-5" />
                Download Guide
              </Button>
              <Button 
                variant="outline"
                className="border-grandpal-green-dark text-grandpal-green-dark hover:bg-grandpal-green-dark/10"
                size="lg"
                onClick={() => setShowPreview(true)}
              >
                <FileText className="mr-2 h-5 w-5" />
                Learn More
              </Button>
            </div>
          </div>

          <div className="relative">
            <div className="absolute inset-0 -rotate-6 bg-grandpal-green-light/30 rounded-lg transform-gpu"></div>
            <div className="absolute inset-0 rotate-3 bg-grandpal-orange/30 rounded-lg transform-gpu"></div>
            <div className="relative bg-white p-6 rounded-lg shadow-lg">
              <img 
                src="/lovable-uploads/58a0be05-26dc-4dff-beff-b488e2839fb6.png" 
                alt="Choosing a Nursing Home in Ireland - 2025 Guide for Families" 
                className="w-full rounded-md shadow"
                onError={(e) => {
                  e.currentTarget.src = '/placeholder.svg';
                }} 
              />
              <div className="mt-6 text-center">
                <h3 className="text-xl font-medium mb-2">Finding a Nursing Home in Ireland</h3>
                <p className="text-grandpal-gray">
                  Our 2025 guide helps families navigate the process of finding the right nursing home in Ireland.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {showPreview && (
        <GuidePreview 
          guide={guidePreview} 
          onClose={() => setShowPreview(false)} 
        />
      )}
    </section>
  );
}
